import type { PreviewReview } from '@flexbyt/shared';
import { reviewsJetWorkspaceId, testimonialsEndpoint } from '../lib/site.js';

/**
 * What ReviewsJet's own customers said.
 *
 * ===========================================================================
 * These have to be real, and that is not negotiable
 * ===========================================================================
 * Every other sample on this site is a product demonstration — obviously
 * illustrative content shown inside a widget to explain what the widget does.
 * This is different. This is the social-proof section, and a social-proof
 * section built from invented quotes is a lie about specific people.
 *
 * So the only source of truth is the API. `fetchTestimonials` reads
 * ReviewsJet's own published reviews from the same public endpoint the embedded
 * widgets call from customers' websites — ReviewsJet showing its own reviews
 * with its own product, which is the most honest version of this section there
 * is and also the best demonstration of it.
 *
 * ===========================================================================
 * When it cannot
 * ===========================================================================
 * The endpoint needs `VITE_REVIEWSJET_WORKSPACE_ID`. Until that is set, the
 * section falls back to `PLACEHOLDER_TESTIMONIALS` below — which are marked as
 * placeholders in the type system, rendered with a visible notice, and kept in
 * this one file so replacing them is deleting a constant.
 *
 * The fallback is never silently presented as customer testimony.
 */

export type TestimonialSet = {
  reviews: PreviewReview[];
  averageRating: number;
  total: number;
  /**
   * `false` when these came from the API, `true` when they are the stand-ins
   * below. Every surface that renders them must branch on this — it is the
   * difference between social proof and a placeholder.
   */
  isPlaceholder: boolean;
};

/**
 * Stand-ins, for a deployment that has not been pointed at a workspace yet.
 *
 * Deliberately not written to be persuasive. They describe the product in flat,
 * checkable terms and are attributed to nobody — no names, no companies, no
 * invented job titles — because a placeholder that looks like a testimonial is
 * one deploy away from becoming a fake one. Anything shipped here should be
 * uncomfortable to leave in production, which is the point.
 */
export const PLACEHOLDER_TESTIMONIALS: PreviewReview[] = [
  {
    id: 'placeholder-1',
    authorName: 'Awaiting real reviews',
    authorAvatar: null,
    rating: 5,
    title: 'This section is not configured yet',
    content:
      'ReviewsJet’s own customer reviews load from the public widget API. Set VITE_REVIEWSJET_WORKSPACE_ID to the workspace that holds them and this section fills itself.',
    reviewDate: '',
    source: 'manual',
    pictures: [],
    isSample: true,
  },
  {
    id: 'placeholder-2',
    authorName: 'Awaiting real reviews',
    authorAvatar: null,
    rating: 5,
    title: 'Nothing here is a customer quote',
    content:
      'These placeholders exist so the layout can be judged before the data arrives. They are not testimonials and must not ship to production.',
    reviewDate: '',
    source: 'manual',
    pictures: [],
    isSample: true,
  },
  {
    id: 'placeholder-3',
    authorName: 'Awaiting real reviews',
    authorAvatar: null,
    rating: 5,
    title: 'Replacing them is deleting a constant',
    content:
      'PLACEHOLDER_TESTIMONIALS lives in src/content/testimonials.ts and is referenced from exactly one place.',
    reviewDate: '',
    source: 'manual',
    pictures: [],
    isSample: true,
  },
];

export const PLACEHOLDER_SET: TestimonialSet = {
  reviews: PLACEHOLDER_TESTIMONIALS,
  averageRating: 0,
  total: 0,
  isPlaceholder: true,
};

/**
 * What the endpoint returns, as far as this cares.
 *
 * Two shapes, because there have been two APIs. The rebuilt one at
 * `app.reviewsjet.com` answers `{ reviews: [...] }` from
 * `/api/public/widget/site-reviews/list`; the old one answered
 * `{ reviews: { docs: [...] } }` from `/api2/widget/site-reviews/list`.
 *
 * Both are accepted rather than picking one, so a leftover override still
 * renders instead of going blank.
 */
type ApiReview = {
  id?: string;
  _id?: string;
  rating?: number;
  title?: string;
  content?: string;
  pictures?: string[];
  reviewDate?: string | null;
  platform?: string;
  reply?: { content?: string } | null;
  reviewer?: { fullName?: string; avatar?: string | null } | null;
};

/**
 * The stored `platform` value, mapped onto a source the renderer knows.
 *
 * Reviews collected through a ReviewsJet form carry `widgetReview` or
 * `via-reviewsjet` rather than the name of an outside platform, because nobody
 * else vouched for them. They get `manual`, which draws no source logo — the
 * honest outcome, since stamping a Google mark on a review that never touched
 * Google is the exact misrepresentation this section must not make.
 */
function toSource(platform: string | undefined): PreviewReview['source'] {
  const known = [
    'google',
    'yelp',
    'tripadvisor',
    'airbnb',
    'trustpilot',
    'etsy',
    'aliexpress',
    'amazon',
    'tourradar',
  ] as const;

  const match = known.find((source) => source === platform);
  return match ?? 'manual';
}

/**
 * Imported Google/Tripadvisor bodies sometimes carry leftover markup (`<br>`,
 * `&amp;`). The testimonials section sets them as type, not as HTML, so those
 * have to become ordinary characters or they show up as tags on the page.
 */
function toPlainText(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+\n/g, '\n')
    .trim();
}

function toPreviewReview(review: ApiReview): PreviewReview {
  return {
    id: String(review.id ?? review._id ?? crypto.randomUUID()),
    authorName: review.reviewer?.fullName?.trim() || 'A ReviewsJet customer',
    authorAvatar: review.reviewer?.avatar || null,
    rating: review.rating ?? 5,
    title: toPlainText(review.title ?? ''),
    content: toPlainText(review.content ?? ''),
    reviewDate: review.reviewDate ?? '',
    source: toSource(review.platform),
    pictures: review.pictures ?? [],
    reply: review.reply?.content ? toPlainText(review.reply.content) : null,
    // These are real reviews by real people. Nothing about them is a sample.
    isSample: false,
  };
}

/**
 * One named review out of the set, for a surface that shows exactly one.
 *
 * ---------------------------------------------------------------------------
 * Why this is looked up rather than pasted
 * ---------------------------------------------------------------------------
 * The homepage prints a single ReviewsJet review as a pull quote. The obvious
 * way to do that is to copy the words into a constant, and it is the one thing
 * this file exists to prevent: a hard-coded quotation attributed to a named
 * customer is a testimonial that keeps claiming to be current after the person
 * has edited it, and keeps existing after they have asked for it to be taken
 * down. Everything shown as testimony here comes from the API, and this is no
 * exception — the constant is the *author*, not the words.
 *
 * ---------------------------------------------------------------------------
 * And why it falls back rather than disappearing
 * ---------------------------------------------------------------------------
 * Matching on a name is a soft link. Somebody can rename their account, the
 * review can be unpublished, the workspace can be repointed — none of which
 * should leave a hole in the homepage. So a miss returns the first review in
 * the set, which is the most recent one and is a perfectly good quotation; the
 * page gets *a* real review either way.
 *
 * Placeholders are the exception and return nothing at all. `PLACEHOLDER_TESTIMONIALS`
 * are deliberately written to be unusable as testimony, and a section that
 * shows a single large quotation has nowhere to put the "these are not real"
 * notice that the grid can carry above itself. Better one missing section than
 * one confident fake quotation.
 */
export function featuredTestimonial(
  set: TestimonialSet,
  authorName: string
): PreviewReview | null {
  if (set.isPlaceholder) return null;

  const wanted = authorName.trim().toLowerCase();
  const named = set.reviews.find(
    (review) => review.authorName.trim().toLowerCase() === wanted
  );

  return named ?? set.reviews[0] ?? null;
}

/**
 * Read ReviewsJet's own published reviews.
 *
 * Called from the route loader, so it runs on the server: the reviews are in
 * the HTML a crawler receives rather than appearing after hydration, which for
 * the social-proof section of a marketing site is the whole point.
 *
 * Returns the placeholder set rather than throwing on any failure. The API
 * being slow or down is not a reason for the homepage to be down, and a
 * marketing page that 500s because a testimonials fetch timed out is a worse
 * outcome than one that shows a visible placeholder.
 */
export async function fetchTestimonials(
  { signal }: { signal?: AbortSignal } = {}
): Promise<TestimonialSet> {
  if (!reviewsJetWorkspaceId) return PLACEHOLDER_SET;

  try {
    const url = new URL(testimonialsEndpoint());
    url.searchParams.set('siteId', reviewsJetWorkspaceId);
    // More than the three quotes on the page, because some published reviews
    // are rating-only and get dropped below. Twenty-four leaves a wall's worth
    // after that filter without downloading the whole workspace.
    url.searchParams.set('limit', '24');
    url.searchParams.set('sortBy', 'recent');

    const response = await fetch(url, {
      signal,
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return PLACEHOLDER_SET;

    const body = (await response.json()) as {
      reviews?: ApiReview[] | { docs?: ApiReview[]; totalDocs?: number };
      total?: number;
    };

    const raw = Array.isArray(body.reviews)
      ? body.reviews
      : (body.reviews?.docs ?? []);
    const reported = Array.isArray(body.reviews)
      ? body.total
      : body.reviews?.totalDocs;

    const reviews = raw
      .map(toPreviewReview)
      // A review with no words in it is a rating, and a wall of empty cards
      // makes the product look broken rather than the account look new.
      .filter((review) => review.content.trim().length > 0);

    if (reviews.length === 0) return PLACEHOLDER_SET;

    const averageRating =
      reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;

    return {
      reviews,
      averageRating: Math.round(averageRating * 10) / 10,
      total: reported ?? reviews.length,
      isPlaceholder: false,
    };
  } catch {
    /*
      Swallowed on purpose, including the abort case. Whatever went wrong —
      DNS, a timeout, a body that is not JSON because a proxy returned an error
      page — the answer for this page is the same, and it is not an exception
      thrown out of a route loader.
    */
    return PLACEHOLDER_SET;
  }
}
